import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import DateSelector from "@features/booking/components/DateSelector";
import { BookingBreadcrumbs } from "@features/booking/components/BookingBreadcrumbs";
import { BookingPageLayout } from "@features/booking/layouts";
import { useLanguage } from "@core/i18n";
import { useBooking } from "@features/booking/state";
import { useCalendarAvailability, getCalendarDayStatus } from "@features/booking/hooks/useCalendarAvailability";
import { CALENDAR_DAY_TYPE_REQUEST } from "@features/booking/types/calendar";
import { buildDateSelectionUpdate, buildServiceResetForDateSelection } from "@features/booking/utils/availability";
import { useTimeSlots } from "@features/booking/hooks/useTimeSlots";
import { useRestaurant } from "@core/tenant/TenantProvider";
import { useAuth } from "@core/auth/AuthProvider";
import { useBookingFlow } from "@features/booking/flow";
import { useAnalytics, ANALYTICS_EVENTS } from "@core/analytics";
import { WalkInDialog } from "@features/booking/components/WalkInDialog";
import type { ServiceType } from "@features/booking/state/types";

const SelectDate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const { dateLocale } = useLanguage();
  const booking = useBooking();
  const { diners, selectedDate, setSelectedDate, specialEvent, updateBooking, meitreNetworkMenuId, calendarIntentCaptured } = booking;
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { goToNext } = useBookingFlow();
  const { trackEvent } = useAnalytics();
  const isEventFlow = !!specialEvent;

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_DATE });
  }, [trackEvent]);

  const [selectedDateIndex, setSelectedDateIndex] = useState<number | null>(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>(
    selectedDate ? new Date(selectedDate + 'T12:00:00') : undefined
  );
  const [walkInDate, setWalkInDate] = useState<Date | null>(null);

  // Get valid service types from restaurant
  const serviceTypes = (restaurant.serviceTypes || []) as ServiceType[];

  // Track whether SERVICE step will be skipped. This is recalculated
  // after date selection when we know real availability.
  const [resolvedSkipService, setResolvedSkipService] = useState(serviceTypes.length === 1);
  const [resolvedSingleService, setResolvedSingleService] = useState<ServiceType | null>(
    serviceTypes.length === 1 ? serviceTypes[0] : null,
  );

  // Fetch calendar availability (will use cached data from SelectDinersPage)
  const {
    mergedCalendar,
    isLoading: isCalendarLoading,
    getDateAvailability,
    getAvailableServicesForDate,
  } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: diners,
    serviceTypes,
    enabled: diners > 0,
    meitreNetworkMenuId,
    preSelectedMenuId: booking.preSelectedMenuId,
    hasPreferredAccess: !!authState?.preferred,
  });

  // Determine available services for prefetch
  const prefetchDate = selectedCalendarDate ? format(selectedCalendarDate, 'yyyy-MM-dd') : null;
  const prefetchServices = useMemo(() => {
    if (!prefetchDate) return [];
    const dayAvail = getDateAvailability(prefetchDate);
    return (dayAvail?.availableServiceTypes || serviceTypes) as ServiceType[];
  }, [prefetchDate, getDateAvailability, serviceTypes]);

  // Prefetch time slots when a date is selected (React Query caches for SelectServicePage)
  const {
    getServiceSlots: prefetchGetServiceSlots,
    slotsByService: prefetchSlotsByService,
    isLoading: isPrefetchLoading,
  } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: diners,
    date: prefetchDate,
    serviceTypes: prefetchServices,
    enabled: !!prefetchDate && prefetchServices.length > 0 && diners > 0,
    hasPreferredAccess: !!authState.preferred,
    meitreNetworkMenuId,
  });

  // --- Deferred navigation for single-service (or single-available-service) ---
  // When SERVICE step is skipped, we need to load slots into booking state
  // BEFORE goToNext() runs, so the flow context has the correct menuCount.
  const [pendingNavigation, setPendingNavigation] = useState(false);

  useEffect(() => {
    if (!pendingNavigation) return;

    if (!resolvedSkipService || !resolvedSingleService) {
      // Multi-service with multiple available: navigate immediately (SERVICE page handles slot loading)
      setPendingNavigation(false);
      goToNext();
      return;
    }

    // Single-service: wait for prefetch to complete
    if (isPrefetchLoading) return;

    const response = prefetchSlotsByService[resolvedSingleService];
    if (!response) return;

    // Phase 1: store slots in booking state (if not yet stored)
    if (booking.timeSlots === null) {
      const slots = prefetchGetServiceSlots(resolvedSingleService);
      booking.setService(resolvedSingleService);
      booking.setTimeSlots(slots.length > 0 ? slots : []);
      booking.setSlotTerms(response.center?.termsAndConditions ?? []);
      booking.setColumnType(response.center?.type ?? null);

      // Detect preferred-only column
      const rawSlots = response.center?.slots ?? [];
      const hasPreferred = response.center?.hasPreferred;
      const thereArePreferred = rawSlots.some((s: { preferred?: boolean; type?: string }) => s.preferred && s.type !== 'No');
      const thereAreNormal = rawSlots.some((s: { preferred?: boolean; type?: string }) => !s.preferred && s.type !== 'No');

      // Refine isEarlyAccessFlow using per-service calendar day type
      // (search-all-hours does NOT return "request" type — only calendar-availability-new does)
      const svcDayAvail = selectedDate ? getDateAvailability(selectedDate) : null;
      const isEarlyAccess = svcDayAvail?.services?.[resolvedSingleService]?.type === CALENDAR_DAY_TYPE_REQUEST;
      updateBooking({
        centerHasOnlyPreferred: !!hasPreferred && thereArePreferred && !thereAreNormal,
        isEarlyAccessFlow: isEarlyAccess,
      });
      return; // Wait for re-render with updated booking state
    }

    // Phase 2: booking state is committed, navigate with fresh flow context
    setPendingNavigation(false);
    goToNext();
  }, [pendingNavigation, resolvedSkipService, resolvedSingleService, isPrefetchLoading, prefetchSlotsByService, prefetchGetServiceSlots, booking.timeSlots, goToNext, booking, updateBooking]);

  // Sync local state with booking state when it changes
  useEffect(() => {
    if (selectedDate) {
      setSelectedCalendarDate(new Date(selectedDate + 'T12:00:00'));
    }
  }, [selectedDate]);

  // Build days array with availability info from calendar.
  // Uses API response dates (mergedCalendar) so the shortcut only shows days
  // the API returns — e.g. today is excluded when past cutoff.
  const days = useMemo(() => {
    let dateSources: Date[];

    if (isEventFlow && specialEvent && specialEvent.dates.length > 0) {
      dateSources = specialEvent.dates.map((d) => new Date(d + 'T12:00:00'));
    } else if (mergedCalendar.length > 0) {
      // Show all days in the shortcut regardless of availability (legacy behavior).
      // Not-available and closed days appear grayed out and disabled.
      // Skip leading closed days so the first shortcut is always an open day.
      const firstOpenIdx = mergedCalendar.findIndex((d) => d.type !== 'not-available-closed');
      const startIdx = firstOpenIdx === -1 ? 0 : firstOpenIdx;
      dateSources = mergedCalendar
        .slice(startIdx, startIdx + 7)
        .map((d) => new Date(d.date + 'T12:00:00'));
    } else {
      // Fallback while calendar is loading
      dateSources = Array.from({ length: 9 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() + i);
        return date;
      });
    }

    const today = format(new Date(), 'yyyy-MM-dd');

    return dateSources.map((date) => {
      const dateKey = format(date, 'yyyy-MM-dd');
      const calendarDay = getDateAvailability(dateKey);
      let displayType = getCalendarDayStatus(calendarDay);
      const availableServices = getAvailableServicesForDate(dateKey);

      // Legacy behavior: shortcut always shows non-closed days as available
      // so the user can select any date and see real availability in the slots.
      if (!meitreNetworkMenuId && !booking.preSelectedMenuId && calendarDay) {
        if (calendarDay.type !== 'not-available-closed') {
          displayType = 'available';
        }
      }

      return {
        day: dateKey === today ? t('TODAY_CAPITALIZED') : format(date, 'EEE', { locale: dateLocale }),
        date: date.getDate(),
        month: format(date, 'MMM', { locale: dateLocale }),
        fullDate: date,
        dateKey,
        displayType,
        hasAvailability: calendarDay?.hasAnyAvailability ?? false,
        isSpecialDay: calendarDay?.isSpecialDay ?? false,
        highlightSpecialDay: calendarDay?.highlightSpecialDay ?? false,
        availableServices,
        calendarDay,
      };
    });
  }, [t, dateLocale, getDateAvailability, getAvailableServicesForDate, isEventFlow, specialEvent, meitreNetworkMenuId, booking.preSelectedMenuId, mergedCalendar]);

  const handleSelectDate = (index: number) => {
    if (pendingNavigation) return; // Prevent double-click while loading

    setSelectedDateIndex(index);
    const newDate = days[index].fullDate;
    setSelectedCalendarDate(newDate);
    setSelectedDate(format(newDate, 'yyyy-MM-dd'));

    // Auto-advance: detect early access + capture intent, then go to next step
    const dateKey = format(newDate, 'yyyy-MM-dd');
    const dayAvail = getDateAvailability(dateKey);

    // Walk-in only: show informational dialog, don't advance
    if (dayAvail?.type === 'walk-in') {
      setWalkInDate(newDate);
      return;
    }

    const { updates, availableServices, singleService } = buildDateSelectionUpdate(dayAvail, serviceTypes);

    const willSkip = !!singleService;
    setResolvedSkipService(willSkip);
    setResolvedSingleService(singleService);

    if (willSkip) {
      booking.setTimeSlots(null);
    }

    const serviceReset = buildServiceResetForDateSelection({
      currentService: booking.service,
      availableServiceTypes: availableServices,
      eventServiceTypes: specialEvent?.serviceTypes as ServiceType[] | undefined,
    });

    updateBooking({
      ...updates,
      ...serviceReset,
    });

    // Defer navigation: the useEffect handles both single-service (waits for
    // slots to load into booking state) and multi-service (navigates immediately).
    setPendingNavigation(true);
  };

  const navigateToFullCalendar = () => {
    navigate("/full-calendar");
  };

  return (
    <BookingPageLayout
      title={t('STRING_SELECT')}
      subtitle={t('STRING_NO_RESULTS_DATE')}
      breadcrumbs={<BookingBreadcrumbs show={['DINERS']} showDinersIcon />}
    >
      <DateSelector
        dates={days}
        selectedDateIndex={selectedDateIndex}
        onSelect={handleSelectDate}
        onShowCalendar={navigateToFullCalendar}
        hideCalendarShortcut={isEventFlow}
        isLoading={isCalendarLoading || pendingNavigation}
      />

      {!isEventFlow && (
        <button
          data-testid="view-full-calendar"
          className="flex items-center gap-2 text-foreground/70 hover:text-brand transition-colors mt-6"
          onClick={navigateToFullCalendar}
        >
          <CalendarIcon size={18} />
          <span className="text-sm font-medium">{t('MORE_DATES')}</span>
        </button>
      )}

      {walkInDate && (
        <WalkInDialog
          open
          onClose={() => setWalkInDate(null)}
          date={walkInDate}
          partySize={diners}
          service={booking.service}
          locale={dateLocale}
        />
      )}
    </BookingPageLayout>
  );
};

export default SelectDate;
